import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  BadgeIndianRupee,
  Download,
  Landmark,
  ShieldCheck,
  UserRound,
  WalletCards,
} from "lucide-react";
import "../../wallet/WalletHistory.css";
import "./WithdrawalListPage.css";

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const WithdrawlHistoryDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_WALLET_PERFIX}/payouts/invoice/${id}`,
          { withCredentials: true },
        );
        setData(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to fetch details");
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchData();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-dark" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center p-5 text-danger">
        {error || "No data available"}
      </div>
    );
  }

  return (
    <section className="withdrawal-page">
      <div className="withdrawal-shell">
        <header className="withdrawal-hero">
          <div className="withdrawal-hero-icon">
            <WalletCards size={24} />
          </div>

          <div className="withdrawal-hero-copy">
            <span className="withdrawal-eyebrow">MY WALLET</span>
            <h1>Withdrawal Details</h1>
            <p>Complete payout information for this withdrawal.</p>
          </div>

          <button
            type="button"
            className="withdrawal-record-download"
            onClick={() => navigate("/WithdrawlHistory")}
            aria-label="Back to Withdrawal History"
          >
            <ArrowLeft size={15} />
            Back
          </button>
          <span className="withdrawal-status withdrawal-status-success">
            Success
          </span>
        </header>

        <div className="wallet-hist-detail-grid">
          <section className="wallet-hist-detail-card wallet-hist-detail-wide">
            <div className="wallet-hist-detail-heading">
              <Landmark size={20} />
              <div>
                <span>WITHDRAWAL INFORMATION</span>
                <h2>Payout Record</h2>
              </div>
            </div>
            <div className="wallet-hist-detail-kv-grid">
              <div>
                <span>W. TXN No.</span>
                <strong>{data.transactionId}</strong>
              </div>
              <div>
                <span>Date</span>
                <strong>{data.payoutDate || data.date}</strong>
              </div>
              <div>
                <span>Incentive Name</span>
                <strong>{data.financialDetails?.description}</strong>
              </div>
              <div>
                <span>Status</span>
                <span className="wallet-hist-status wallet-hist-status-completed">
                  Success
                </span>
              </div>
            </div>
          </section>

          <section className="wallet-hist-detail-card">
            <div className="wallet-hist-detail-heading">
              <UserRound size={20} />
              <div>
                <span>RECEIVED FROM</span>
                <h2>Partner Information</h2>
              </div>
            </div>
            <div className="wallet-hist-detail-party-row">
              <span className="wallet-hist-detail-party-avatar">
                <UserRound size={20} />
              </span>
              <div>
                <span>PARTNER NAME</span>
                <strong>{data.customerDetails?.name || "N/A"}</strong>
              </div>
            </div>
          </section>

          <section className="wallet-hist-detail-card">
            <div className="wallet-hist-detail-heading">
              <BadgeIndianRupee size={20} />
              <div>
                <span>AMOUNT INFORMATION</span>
                <h2>Transferred Amount</h2>
              </div>
            </div>
            <div className="wallet-hist-detail-highlight">
              <span>TRANSFERRED AMOUNT</span>
              <strong>
                {formatCurrency(Number(data.financialDetails?.netAmount || 0))}
              </strong>
              <small>Net payout credited to your account</small>
            </div>
          </section>
        </div>

        <section className="wallet-hist-detail-footer-card">
          <div>
            <ShieldCheck size={20} />
            <div>
              <strong>Payout record secured</strong>
              <span>
                This withdrawal is linked to your Salar wallet payout
                history.
              </span>
            </div>
          </div>
          <Link to={`/CustRefPayInvoice/${data.transactionId}`}>
            <Download size={15} />
            Download Payout Receipt
          </Link>
        </section>
      </div>
    </section>
  );
};

export default WithdrawlHistoryDetails;
